import { AutoComplete as AntAutoComplete, Empty, Spin, Tooltip } from 'antd'
import { CheckCircleFilled, SearchOutlined } from '@ant-design/icons'
import { gql, TypedDocumentNode, useLazyQuery } from '@apollo/client'
import { ReactElement, ReactNode, useRef, useState } from 'react'
import { Contact } from '../type/contact'
import { ContactFindRequest } from '../type/contact.query'
import { QueryPageableRequest, QueryPageableResponse } from '../../../core/type'
import { translate } from '../../../core'

const FindContactsForRelation: TypedDocumentNode<
  QueryPageableResponse<Contact, 'contactsFind'>,
  QueryPageableRequest<ContactFindRequest>
> = gql`
  query FindContactsForRelation($request: ContactFindRequest!) {
    contactsFind(request: $request) {
      data {
        id
        contactId
        company
        firstName
        lastName
        phones {
          type
          number
        }
        addresses {
          type
          city
          country
          street
          zipCode
          state
          line1
          line2
        }
        webInfos {
          type
          value
        }
      }
    }
  }
`

type Option = {
  value: string
  label: ReactNode
  contact: Contact
}

type Props = {
  value?: string
  onChange?: (value: string) => void
  onContactPicked: (contact: Contact | null) => void
  isLinked?: boolean
  disabled?: boolean
}

const ContactOptionRow = ({ contact }: { contact: Contact }) => {
  const [address] = contact.addresses ?? []
  const [phone] = contact.phones ?? []
  const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
  const hasContactId = !!contact.contactId && contact.contactId.trim() !== '' && contact.contactId.trim() !== '-'

  const primary = hasContactId
    ? contact.contactId
    : fullName || contact.company || `#${contact.id}`

  const secondaryParts = hasContactId
    ? [fullName || contact.company, phone?.number, address?.city]
    : [phone?.number, address?.city]

  const secondary = secondaryParts.filter(Boolean).join(' · ')

  const ellipsis: React.CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
      <span style={{ fontWeight: 600, color: '#111', fontSize: 15, ...ellipsis }}>
        {primary}
      </span>
      {secondary && (
        <span style={{ fontSize: 14, color: '#888', ...ellipsis }}>
          {secondary}
        </span>
      )}
    </div>
  )
}

export const displayValueFor = (c: Contact): string => {
  const id = c.contactId?.trim()
  if (id && id !== '-') return id
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim()
  if (name) return name
  if (c.company?.trim()) return c.company.trim()
  return `#${c.id}`
}

// Option values must be unique (antd/rc-select keys by value). Two contacts
// may share the same displayValueFor result (e.g., two "John Doe" records
// with no contactId), so disambiguate with the numeric id. The parent
// overrides the contactId form field with displayValueFor(contact) on select,
// so the user never sees the suffix in the input.
const optionValueFor = (c: Contact): string => `${displayValueFor(c)} #${c.id}`

const mapResults = (list: Contact[]): Option[] =>
  list.map((c) => ({
    value: optionValueFor(c),
    label: <ContactOptionRow contact={c} />,
    contact: c,
  }))

export const ContactRelationSelect = ({
  value,
  onChange,
  onContactPicked,
  isLinked,
  disabled,
}: Props) => {
  const [options, setOptions] = useState<Option[]>([])
  const [searchText, setSearchText] = useState<string>('')
  const [open, setOpen] = useState<boolean>(false)
  const lastPickedRef = useRef<string>('')
  const initialLoadedRef = useRef<boolean>(false)
  const requestIdRef = useRef<number>(0)

  const [find, { loading }] = useLazyQuery(FindContactsForRelation)

  const runQuery = async (text: string) => {
    const myRequestId = ++requestIdRef.current
    const trimmed = text.trim()
    try {
      const { data } = await find({
        variables: {
          request: {
            take: 20,
            skip: 0,
            contactId: trimmed || undefined,
          },
        },
      })
      if (myRequestId !== requestIdRef.current) return
      setOptions(mapResults(data?.contactsFind.data ?? []))
    } catch {
      if (myRequestId !== requestIdRef.current) return
      setOptions([])
    }
  }

  const search = async (text: string) => {
    setSearchText(text)
    await runQuery(text)
  }

  const handleDropdownOpen = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) return
    if (initialLoadedRef.current && options.length) return
    initialLoadedRef.current = true
    void runQuery(searchText)
  }

  const handleSelect = (picked: string, option: any) => {
    lastPickedRef.current = picked
    setOpen(false)
    onContactPicked((option as Option).contact)
  }

  const handleChange = (next: string, option?: Option | Option[]) => {
    onChange?.(next)
    if (option && !Array.isArray(option)) {
      return
    }
    if (lastPickedRef.current !== '' || isLinked) {
      lastPickedRef.current = ''
      onContactPicked(null)
    }
  }

  const dropdownRender = (menu: ReactElement) => {
    if (loading && !options.length) {
      return (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <Spin size="small" />
        </div>
      )
    }
    if (options.length) return menu
    if (searchText) {
      return (
        <div style={{ padding: '12px 8px' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: 13, color: '#888' }}>
                {translate('no_matching_contacts')}
              </span>
            }
            imageStyle={{ height: 36 }}
          />
        </div>
      )
    }
    return (
      <div
        style={{
          padding: 14,
          textAlign: 'center',
          color: '#999',
          fontSize: 13,
        }}
      >
        <SearchOutlined style={{ marginRight: 6 }} />
        {translate('type_to_search_contacts')}
      </div>
    )
  }

  const suffixIcon = isLinked ? (
    <Tooltip title={translate('linked_to_existing_contact')}>
      <CheckCircleFilled style={{ color: '#52c41a' }} />
    </Tooltip>
  ) : (
    <SearchOutlined style={{ color: '#bbb' }} />
  )

  return (
    <AntAutoComplete
      value={value}
      options={options}
      onSearch={search}
      onSelect={handleSelect}
      onChange={handleChange}
      open={open}
      onDropdownVisibleChange={handleDropdownOpen}
      disabled={disabled}
      allowClear
      style={{ width: '100%' }}
      popupClassName="contact-relation-select-dropdown"
      popupMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 260, maxWidth: 400 }}
      getPopupContainer={(trigger) => (trigger.parentElement as HTMLElement) ?? document.body}
      placeholder={translate('search_contact_placeholder')}
      suffixIcon={suffixIcon}
      dropdownRender={dropdownRender}
    />
  )
}
